/**
 * Mock payload for `GET /v1/inventory/insights/actionable/`.
 *
 * Six rules from the ONE-3510 catalogue, covering both AIM missions and all four
 * severity tiers.
 *
 * ### The numbers are calibrated against production, and the names are not
 *
 * Scopes, affected counts and magnitudes were checked against the production
 * OpenSearch indices so the deck renders proportions a real tenant produces.
 * Every identifier below is invented. Hostnames and UPNs match the SHAPE of real
 * ones (uppercase, hyphenated, two segments, 12 to 14 characters; admin local
 * parts of 4 to 13 letters, roughly a fifth carrying an `adm` marker) and none
 * of them is a customer value. `northwind.example` is a reserved TLD, so it can
 * never resolve.
 *
 * ### Three of these replace the old coverage cards
 *
 * This deck replaces the coverage-gap cards that used to sit under the page
 * header, so the ones worth keeping moved across: AIM-COV-003 (no EDR),
 * AIM-COV-004 (no vulnerability coverage) and AIM-IDENT-004 (active but not in
 * the directory). Each names a specific missing source, which is what makes it
 * a finding.
 *
 * The two "Low Coverage" cards were deliberately NOT carried over. They fired on
 * `source_count = 1`, which in production is 81.5% of users and 97.6% of
 * endpoints. That is a statement about our integration footprint, not a finding
 * about any asset, and moving it into a deck that rotates would have put the
 * noisiest card in the product on screen every minute.
 *
 * ### The case rules are measured too
 *
 * `AlertStat` carries a structured link from a case to a username (25% of rows)
 * and to an endpoint (8%), so both MDR rules were measured directly rather than
 * inferred. Across 90 days of production: 1,403 endpoints carry at least one
 * confirmed incident and 11% of them carry three or more, running to 26% in the
 * worst tenant. Identity case volume per tenant has a median of 1 and a long
 * tail reaching 3,295. The two case cards below sit inside those ranges.
 *
 * ### What the calibration changed
 *
 * The privileged-MFA rule used to read 4 of 61 admins, which is 7%. Production
 * says the median tenant has 10 admins, the ninetieth percentile has 23, and
 * where the rule fires at all it fires on 19% to 71% of them. A 7% reading made
 * the highest-weighted check in the catalogue look like a rounding error. It now
 * reads 5 of 16.
 *
 * The endpoint scopes describe one coherent mid-to-large tenant: a fleet around
 * 890, which sits near the ninetieth percentile (the median fleet is 61, the
 * largest is 12,018). A median tenant would have been more typical and would
 * have shown almost nothing on screen.
 *
 * ### Proportions span two orders of magnitude, on purpose
 *
 * From 0.4% (3 of 842) to 31% (5 of 16). The constellation exists to make that
 * spread legible, so mock data clustered around one proportion would demo the
 * layout while proving nothing about the thing the layout is for.
 */

import { ActionableInsightsResponse } from '../models/actionable-insights.models';

export const mockActionableInsights: ActionableInsightsResponse = {
  generated_at: '2026-08-25T14:00:00Z',
  insights: [
    {
      rule_id: 'AIM-IDENT-001',
      accepted_count: 0,
      sample_entities: [
        { name: 'jtorres_adm@northwind.example', detail: 'Global Administrator', weight: null },
        { name: 'helpdesk@northwind.example', detail: 'Helpdesk Administrator', weight: null },
        { name: 'r.okafor@northwind.example', detail: 'Exchange Administrator', weight: null },
        { name: 'mdelacruz_adm@northwind.example', detail: 'Security Administrator', weight: null },
      ],
      short_title: 'Admin MFA',
      title: 'Privileged accounts without MFA',
      severity: 'critical',
      mission: 'standalone',
      entity_type: 'user',
      affected_count: 5,
      scope_count: 16,
      new_since_last: 1,
      why: 'Directory roles that can change tenant-wide settings, with only a password in front of them.',
      action: 'Enforce MFA, or remove the role from accounts that no longer need it.',
      evidence: null,
      tab: 'users',
      filter_key: 'privileged_no_mfa',
    },
    {
      rule_id: 'AIM-LIFE-002',
      accepted_count: 0,
      sample_entities: [
        { name: 'NWK-FINDB002', detail: 'Agent gone 6 days', weight: 6 },
        { name: 'NWK-HRWS0114', detail: 'Agent gone 3 days', weight: 3 },
        { name: 'NWK-ENGWS0207', detail: 'Agent gone 2 days', weight: 2 },
      ],
      short_title: 'Protection lost',
      title: 'Protection was removed',
      severity: 'critical',
      mission: 'both',
      entity_type: 'endpoint',
      affected_count: 3,
      scope_count: 842,
      new_since_last: 3,
      why: 'These reported an EDR agent last week and do not now. Something changed and nobody recorded it.',
      action: 'Find out who removed each agent, and whether it was intended.',
      evidence: null,
      tab: 'endpoints',
      filter_key: 'coverage_regression',
    },
    {
      rule_id: 'AIM-COV-003',
      accepted_count: 4,
      sample_entities: [
        { name: 'NWK-LABWS0221', detail: 'Seen by XDR only', weight: null },
        { name: 'NWK-OPSWS0198', detail: 'Seen by Vicarius only', weight: null },
        { name: 'NWK-ENGWS0402', detail: 'Seen by XDR only', weight: null },
        { name: 'NWK-FINWS0163', detail: 'Seen by Vicarius only', weight: null },
      ],
      short_title: 'No EDR',
      title: 'Endpoints with no EDR agent',
      severity: 'critical',
      mission: 'standalone',
      entity_type: 'endpoint',
      affected_count: 23,
      scope_count: 871,
      new_since_last: 2,
      why: 'Another tool can see these hosts, so they exist and are running. Nothing is watching them for threats.',
      action: 'Deploy an agent, or accept the ones that cannot take one and say why.',
      evidence: null,
      tab: 'endpoints',
      filter_key: 'no_edr',
    },
    {
      rule_id: 'AIM-CASE-001',
      accepted_count: 2,
      sample_entities: [
        { name: 'NWK-EXECLT003', detail: '14 confirmed in 90 days', weight: 14 },
        { name: 'NWK-SALESWS041', detail: '11 confirmed in 90 days', weight: 11 },
        { name: 'NWK-WHKIOSK01', detail: '9 confirmed in 90 days', weight: 9 },
        { name: 'NWK-SALESWS017', detail: '6 confirmed in 90 days', weight: 6 },
      ],
      short_title: 'Repeat incidents',
      title: 'Assets with repeated confirmed incidents',
      severity: 'high',
      mission: 'mdr',
      entity_type: 'endpoint',
      affected_count: 18,
      scope_count: 169,
      new_since_last: 2,
      why: 'Three or more confirmed true positives each, in 90 days. That is a control problem, not bad luck.',
      action: 'Review the control set on these hosts before the next incident.',
      evidence: '112 incidents our SOC closed as confirmed on these 18 hosts.',
      tab: 'endpoints',
      filter_key: 'repeat_true_positive',
    },
    {
      rule_id: 'AIM-HEALTH-001',
      accepted_count: 9,
      sample_entities: [
        { name: 'NWK-ENGWS0188', detail: 'Silent 41 days', weight: 41 },
        { name: 'NWK-FINWS0022', detail: 'Silent 33 days', weight: 33 },
        { name: 'NWK-OPSWS0310', detail: 'Silent 28 days', weight: 28 },
        { name: 'NWK-HRWS0067', detail: 'Silent 19 days', weight: 19 },
      ],
      short_title: 'Silent agents',
      title: 'Agents installed but not reporting',
      severity: 'high',
      mission: 'both',
      entity_type: 'endpoint',
      affected_count: 26,
      scope_count: 871,
      new_since_last: 0,
      why: 'Coverage counts treat a silent agent like a healthy one, so these sit on the protected side of every report.',
      action: 'Check whether each host is off, retired, or failed. Flag the intentional ones.',
      evidence: null,
      tab: 'endpoints',
      filter_key: 'agent_silent',
    },
    {
      rule_id: 'AIM-CASE-002',
      accepted_count: 4,
      sample_entities: [
        { name: 'billing@northwind.example', detail: '284 cases, unclassified', weight: 284 },
        { name: 'a.whitfield@northwind.example', detail: '196 cases, Finance', weight: 196 },
        { name: 'j.moreau@northwind.example', detail: '151 cases, Sales', weight: 151 },
        { name: 't.nakamura@northwind.example', detail: '88 cases, Support', weight: 88 },
      ],
      short_title: 'Case hotspots',
      title: 'Case volume concentrated on one identity',
      severity: 'medium',
      mission: 'mdr',
      entity_type: 'user',
      affected_count: 33,
      scope_count: 342,
      new_since_last: 4,
      why: 'Top decile of case volume here. Usually a person who needs help, an account being targeted, or a shared account filed as a person.',
      action: 'Check the classifications. Anything that is not a person should be reclassified.',
      evidence: 'Ranked against this tenant\'s 342 identities with case history. The median carries 1.',
      tab: 'users',
      filter_key: 'case_concentration',
    },
    {
      rule_id: 'AIM-COV-004',
      accepted_count: 7,
      sample_entities: [
        { name: 'NWK-HRWS0244', detail: 'EDR agent, no scanner', weight: null },
        { name: 'NWK-SALESWS088', detail: 'EDR agent, no scanner', weight: null },
        { name: 'NWK-EXECLT011', detail: 'EDR agent, no scanner', weight: null },
        { name: 'NWK-OPSWS0355', detail: 'EDR agent, no scanner', weight: null },
      ],
      short_title: 'No scanner',
      title: 'Endpoints with no vulnerability coverage',
      severity: 'medium',
      mission: 'standalone',
      entity_type: 'endpoint',
      affected_count: 56,
      scope_count: 871,
      new_since_last: 0,
      why: 'These carry an EDR agent, so they are protected against threats, but nothing is checking them for known vulnerabilities.',
      action: 'Extend the vulnerability scanner to these hosts.',
      evidence: null,
      tab: 'endpoints',
      filter_key: 'no_vicarius',
    },
    {
      rule_id: 'AIM-IDENT-004',
      accepted_count: 0,
      sample_entities: [
        { name: 'sql_report_svc@northwind.example', detail: 'XDR only, no directory record', weight: null },
        { name: 'kiosk_shared@northwind.example', detail: 'XDR only, no directory record', weight: null },
        { name: 'legacy_ftp@northwind.example', detail: 'XDR only, no directory record', weight: null },
      ],
      short_title: 'Not in directory',
      title: 'Users active but not in the directory',
      severity: 'medium',
      mission: 'standalone',
      entity_type: 'user',
      affected_count: 12,
      scope_count: 1412,
      new_since_last: 1,
      why: 'XDR sees these accounts authenticating, and Entra ID has no record of them. They are service, shadow or external accounts nobody is governing.',
      action: 'Identify each one. Anything legitimate should be classified as a service account; anything else should be disabled.',
      evidence: null,
      tab: 'users',
      filter_key: 'no_entra_id',
    },
    {
      rule_id: 'AIM-CONF-001',
      accepted_count: 11,
      sample_entities: [
        { name: 'NWK-ENGWS0145', detail: 'EDR says Win 11, VSS says Win 10', weight: null },
        { name: 'NWK-FINWS0009', detail: 'EDR says Win 10, VSS says Win 11', weight: null },
        { name: 'NWK-OPSWS0276', detail: 'Architecture disagrees', weight: null },
        { name: 'NWK-HRWS0131', detail: 'EDR says Win 11, VSS says Win 10', weight: null },
      ],
      short_title: 'Source conflicts',
      title: 'Sources disagree about an asset',
      severity: 'low',
      mission: 'standalone',
      entity_type: 'endpoint',
      affected_count: 34,
      scope_count: 178,
      new_since_last: 6,
      why: 'Two tools describe the same host and report different operating systems.',
      action: 'Work the review queue. Confirm the merge, or split the entity.',
      evidence: null,
      tab: 'endpoints',
      filter_key: 'source_conflict',
    },
  ],
};
